import React, { useState, useEffect, createContext } from 'react';
import { authAPI, userAPI } from '../utils/api';
import { message } from 'antd';

// 创建认证上下文
const AuthContext = createContext();

// 认证提供者组件
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 初始化时检查登录状态
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // 检查认证状态
  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const userData = await authAPI.getCurrentUser();
        setUser(userData.data);
      }
    } catch (error) {
      console.error('认证检查失败:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // 登录
  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);

      // 检查后端返回的success字段，如果是false说明登录失败
      if (response && response.success === false) {
        const errorMsg = response.message || '登录失败，请检查用户名和密码';
        message.error(errorMsg);
        throw new Error(errorMsg);
      }

      // 成功情况下，从data字段获取token和user
      const data = response.data;
      if (!data || !data.token || !data.user) {
        message.error('登录响应数据结构错误');
        throw new Error('登录响应数据结构错误');
      }

      // 存储真实的token和用户信息
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('current_user', JSON.stringify(data.user));
      setUser(data.user);
      message.success('登录成功');
      return response;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '登录失败，请检查网络连接';
      message.error(errorMessage);
      throw error;
    }
  };

  // 注册
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);

      // 检查后端返回的success字段，如果是false说明注册失败
      if (response && response.success === false) {
        const errorMsg = response.message || '注册失败';
        message.error(errorMsg);
        throw new Error(errorMsg);
      }

      // 注册后自动登录，后端在注册接口中可能会返回token或需要单独的登录
      const loginResponse = await authAPI.login({
        username: userData.username,
        password: userData.password
      });

      // 检查登录响应
      if (loginResponse && loginResponse.success === false) {
        message.error('注册成功，但自动登录失败，请手动登录');
        return response;
      }

      const data = loginResponse.data;
      if (!data || !data.token || !data.user) {
        message.error('登录响应数据结构错误');
        throw new Error('登录响应数据结构错误');
      }

      // 存储真实的token和用户信息
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('current_user', JSON.stringify(data.user));
      setUser(data.user);
      message.success('注册成功并已自动登录');
      return response;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '注册失败';
      message.error(errorMessage);
      throw error;
    }
  };

  // 退出登录
  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    setUser(null);
    message.success('已退出登录');
  };

  // 刷新token
  const refreshToken = async () => {
    try {
      const response = await authAPI.refreshToken();
      if (response.success) {
        localStorage.setItem('auth_token', response.token);
        return true;
      }
    } catch (error) {
      console.error('Token刷新失败:', error);
      logout();
      return false;
    }
  };

  // 检查用户权限
  const hasPermission = (permission) => {
    if (!user) return false;
    // 这里可以根据用户角色或权限进行判断
    // 暂时对所有已登录用户返回 true，后续可根据实际权限系统实现
    return user.permissions ? user.permissions.includes(permission) : false;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    hasPermission,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// 导出上下文供其他文件使用
export { AuthContext };
